/**
 * Almikia Epic E7 (TASK-090): calendar-month / calendar-quarter period
 * helpers in America/Mexico_City local time (DOMAIN RULE (b), ASSUMPTION:
 * monthly and quarterly incentives are never cumulative across periods).
 * Every period boundary is derived from LOCAL calendar dates, never from
 * UTC instants directly, so "the last local day of the month" is exact
 * regardless of the host machine's own timezone.
 */

export const MEXICO_CITY_TIME_ZONE = "America/Mexico_City";

/** A local (year/month/day) calendar date, `month` 1-12. Carries no time-of-day. */
export type LocalDate = {
  year: number;
  month: number;
  day: number;
};

export type PeriodKind = "month" | "quarter" | "accumulated";

/** An inclusive `[start, end]` local-date window. */
export type Period = {
  kind: PeriodKind;
  start: LocalDate;
  end: LocalDate;
};

/** Resolves `instant`'s local calendar date in `timeZone` (default Mexico City). */
export function getLocalDateParts(
  instant: Date,
  timeZone: string = MEXICO_CITY_TIME_ZONE,
): LocalDate {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instant);
  const byType = Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );
  return {
    year: Number(byType.year),
    month: Number(byType.month),
    day: Number(byType.day),
  };
}

/**
 * Parses a plain `YYYY-MM-DD` date-only string (e.g.
 * `deals.expected_closing_date`, `incentive_programs.valid_from`) with no
 * timezone conversion -- it is already a calendar date, not an instant.
 */
export function localDateFromDateOnlyString(dateOnly: string): LocalDate {
  const [year, month, day] = dateOnly.split("-").map(Number);
  return { year, month, day };
}

/** Resolves an ISO timestamptz instant (e.g. `deals.updated_at`) to its local calendar date. */
export function localDateFromInstant(
  isoInstant: string,
  timeZone: string = MEXICO_CITY_TIME_ZONE,
): LocalDate {
  return getLocalDateParts(new Date(isoInstant), timeZone);
}

/** Chronological order: negative when `a` is before `b`, positive when after, 0 when equal. */
export function compareLocalDates(a: LocalDate, b: LocalDate): number {
  return a.year - b.year || a.month - b.month || a.day - b.day;
}

/** True when `date` falls within `period`'s inclusive `[start, end]` window. */
export function isLocalDateWithinPeriod(
  date: LocalDate,
  period: Period,
): boolean {
  return (
    compareLocalDates(date, period.start) >= 0 &&
    compareLocalDates(date, period.end) <= 0
  );
}

/** The last day-of-month number for `year`/`month` (1-12) -- plain calendar arithmetic, not an instant. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * The calendar month containing `now`'s Mexico City local date (DOMAIN RULE
 * (b): a period starts from zero, nothing carries over from the previous
 * one).
 */
export function getCurrentMonthPeriod(
  now: Date = new Date(),
  timeZone: string = MEXICO_CITY_TIME_ZONE,
): Period {
  const { year, month } = getLocalDateParts(now, timeZone);
  return {
    kind: "month",
    start: { year, month, day: 1 },
    end: { year, month, day: daysInMonth(year, month) },
  };
}

/**
 * The calendar quarter (Jan-Mar, Apr-Jun, Jul-Sep, Oct-Dec) containing
 * `now`'s Mexico City local date.
 */
export function getCurrentQuarterPeriod(
  now: Date = new Date(),
  timeZone: string = MEXICO_CITY_TIME_ZONE,
): Period {
  const { year, month } = getLocalDateParts(now, timeZone);
  const startMonth = Math.floor((month - 1) / 3) * 3 + 1;
  const endMonth = startMonth + 2;
  return {
    kind: "quarter",
    start: { year, month: startMonth, day: 1 },
    end: { year, month: endMonth, day: daysInMonth(year, endMonth) },
  };
}

/**
 * A single-day window on `now`'s local date. Used only to check an
 * `accumulated`-period program's validity window (DOMAIN RULE (j)) -- it
 * carries no sales data of its own (e.g. `gasoline_insurance`).
 */
export function getAccumulatedPointPeriod(
  now: Date = new Date(),
  timeZone: string = MEXICO_CITY_TIME_ZONE,
): Period {
  const today = getLocalDateParts(now, timeZone);
  return { kind: "accumulated", start: today, end: today };
}

/**
 * Whole calendar days left in `period` (0 once its last day has passed),
 * counted from `now`'s Mexico City local date.
 */
export function daysLeftInPeriod(
  period: Period,
  now: Date = new Date(),
  timeZone: string = MEXICO_CITY_TIME_ZONE,
): number {
  const today = getLocalDateParts(now, timeZone);
  const todayUTC = Date.UTC(today.year, today.month - 1, today.day);
  const endUTC = Date.UTC(
    period.end.year,
    period.end.month - 1,
    period.end.day,
  );
  return Math.max(0, Math.round((endUTC - todayUTC) / (24 * 60 * 60 * 1000)));
}

/** `period`'s first calendar day as a `YYYY-MM-DD` string, matching `advisor_goals.period_month`. */
export function periodMonthKey(period: Period): string {
  const { year, month } = period.start;
  return `${year}-${String(month).padStart(2, "0")}-01`;
}
